<script setup lang="tsx">
import { ref, computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import EditDeleteButtons from '../widgets/EditDeleteButtons.vue'
import CancelSaveButtons from '../widgets/CancelSaveButtons.vue'
import InstanceSelect from '../widgets/InstanceSelect.vue'
import DataAssetConnections from './DataAssetConnections.vue'
import sliderStore from '@/stores/slider'
import toastStore from '@/stores/toast'
import http from '@/services/http'
import type { CustomFieldDefinition, DataAsset, DataAssetWithConnections, Response, RiskShort } from '@/models/api-generated'
import { dataAssetRefreshCallback } from '@/components/business_processes/shared'
import Datepicker from '@vuepic/vue-datepicker'
import { fetchCustomValues, type CustomValueMap } from '@/services/custom'
import { defaultInstance, formatLocale, genRouteID, getDateFormat, hasPermissionOwn, viewing } from '@/services/utils'
import {
	DataAssetReference,
	PermAllDataAssetDelete,
	PermAllDataAssetWrite,
	PermOwnDataAssetDelete,
	PermOwnDataAssetWrite,
	RT_Exclude,
} from '@/models/enums-generated'
import CustomValueEdit from '../custom_field/CustomValueEdit.vue'
import CustomValueDisplay from '../custom_field/CustomValueDisplay.vue'
import ResponsibleSelect from '../widgets/ResponsibleSelect.vue'
import DocumentReference from '../comment_references/DocumentReference.vue'
import URLReference from '../comment_references/URLReference.vue'
import { useTheme } from '../user/useTheme'
import { scoreToLevel } from '../risk_management/shared'

const { theme } = useTheme()
const defaultAsset: DataAsset = {
	id: 0,
	name: '',
	date: null,
	location: '',
	hasPersonalData: false,
	instance: defaultInstance(),
	customFields: {},
	isChecked: false,
	urlReferences: [],
	documentReferences: [],
	risks: [],
}

const defaultAssetConnections: DataAssetWithConnections = {
	dataAsset: defaultAsset,
	businessProcesses: [],
	assets: [],
}

const props = defineProps<{ assetId: number }>()
const { t } = useI18n()
const slider = sliderStore()
const toaster = toastStore()
const customValues = ref<CustomValueMap>()
const isAdd = computed(() => props.assetId === 0)
const isEdit = ref(false)
const model = ref<DataAssetWithConnections>({ ...defaultAssetConnections })
const isChangeMode = computed(() => isAdd.value || isEdit.value)
const canEdit = computed(() => hasPermissionOwn(model.value.dataAsset.responsible, PermAllDataAssetWrite, PermOwnDataAssetWrite))
const canDelete = computed(() => hasPermissionOwn(model.value.dataAsset.responsible, PermAllDataAssetDelete, PermOwnDataAssetDelete))
const canEditInstance = computed(() => viewing.value === 0)
const isValidInstance = computed(() => viewing.value !== 0 || model.value.dataAsset.instance?.id > 0)
const isValidName = computed(() => model.value.dataAsset.name.trim().length > 0)
const canSave = computed(() => isValidName.value && isValidInstance.value)
const custom = ref<CustomFieldDefinition[]>([])

onMounted(async () => {
	await refresh()
	slider.watchChanges(model)
})

watch(() => props.assetId, refresh)
watch(isEdit, (edit) => slider.setFooter(edit ? editBtns : viewBtns))

const editBtns = (
	<CancelSaveButtons saveId="save-data-asset" cancelId="cancel-data-asset" canSave={canSave} onCancel={cancel} onSave={save} />
)
const viewBtns = (
	<EditDeleteButtons
		editId="open-edit-dataasset"
		deleteId="delete-dataasset"
		canEdit={canEdit}
		onEdit={edit}
		canDelete={canDelete}
		onDelete={remove}
	/>
)

async function edit() {
	isEdit.value = true
}

async function refresh() {
	isEdit.value = false
	if (!isAdd.value) {
		const { data } = await http.get<Response<DataAssetWithConnections>>(`/api/dataasset/${props.assetId}`)
		if (data.data) model.value = data.data
		slider.setFooter(viewBtns)
	} else {
		model.value = defaultAssetConnections
		slider.setFooter(editBtns)
		if (viewing.value > 0) {
			model.value.dataAsset.instance.id = viewing.value
		}
	}
	await getCustomFields()
}

async function getCustomFields() {
	const { data } = await http.get<Response<CustomFieldDefinition[]>>(`api/customFields/data_assets`)
	if (data.data?.length) custom.value.push(...data.data)

	if (!isAdd.value) customValues.value = await fetchCustomValues('data_assets', props.assetId)
}

async function cancel() {
	const doCancel = async () => {
		if (isAdd.value) {
			slider.close()
		} else {
			await refresh()
		}
	}

	if (slider.getHasUnsavedChanges()) {
		slider.requestCancel(doCancel)
	} else {
		await doCancel()
	}
}

function remove() {
	slider.requestDeleteConfirm(t('misc.confirmDelete'), 'dataasset', confirmDelete)
}

async function confirmDelete() {
	const { status } = await http.get(`/api/dataasset/delete/${genRouteID(model.value.dataAsset.id, '', 'DataAsset')}`)
	if (status === 200) {
		toaster.showSuccess(t('misc.successfullyDeleted'))
		slider.setUnsavedChanges(false)
		slider.close()
		dataAssetRefreshCallback.value?.forEach((fn) => fn())
	}
}

async function save() {
	const { status } = await http.post<Response<DataAsset>>('/api/dataasset', model.value.dataAsset)
	if (status === 200) {
		toaster.showSuccess(t(isAdd.value ? 'misc.successfullyAdded' : 'misc.successfullyEdited'))
		slider.setUnsavedChanges(false)
		slider.close()
		dataAssetRefreshCallback.value?.forEach((fn) => fn())
	}
}

function updateCustomValue(fieldId: number, value: string) {
	customValues.value ??= {}
	customValues.value[fieldId] ??= {}
	customValues.value[fieldId][model.value.dataAsset.id] = value
	model.value.dataAsset.customFields ||= {}
	model.value.dataAsset.customFields[fieldId] ??= {}
	model.value.dataAsset.customFields[fieldId][model.value.dataAsset.id] = value
}

const defaultConnectionRisks = computed(() => {
	return model.value.dataAsset.risks.filter((r) => !r.assetId)
})

const assetConnectionRisks = computed(() => {
	return model.value.dataAsset.risks.filter((r) => r.assetId)
})

function goToRisks(names: string[]) {
	if (viewing.value === 0) return
	const params = new URLSearchParams()
	names.forEach((name) => {
		params.append('name', name)
	})
	window.location.href = `/app/risk-management?${params}`
}

const riskColor = (risk: RiskShort) => {
	return risk.treatment === RT_Exclude ? 'gray' : scoreToLevel(risk.residual)?.color || 'gray'
}
</script>

<template>
	<div :class="{ striped: !isChangeMode }" data-test="data-asset-slide">
		<div class="row">
			<label class="col-4 col-form-label">{{ t('misc.name') }}</label>
			<div class="col-8 d-flex flex-column">
				<input
					v-if="isChangeMode"
					data-test="data-asset-name"
					class="form-control w-100 mb-1"
					v-model="model.dataAsset.name"
					:class="{ 'is-invalid': !isValidName }"
					required
				/>
				<div v-if="isChangeMode" class="invalid-feedback">{{ $t('misc.validName') }}</div>
				<div v-else class="d-flex align-items-center justify-content-end flex-grow-1">
					<span class="text-end">{{ model.dataAsset.name || '-' }}</span>
				</div>
			</div>
		</div>

		<div class="row">
			<label class="col-4 col-form-label">{{ t('responsible') }}</label>
			<div class="col-8 d-flex align-items-center justify-content-end">
				<ResponsibleSelect v-if="isChangeMode" data-test="data-asset-owner" v-model="model.dataAsset.responsible" class="mb-2" />
				<span v-else>{{ model.dataAsset.responsible?.label || '-' }}</span>
			</div>
		</div>

		<div class="row">
			<label class="col-4 col-form-label">{{ t('date.date') }}</label>
			<div class="col-8 d-flex align-items-center justify-content-end">
				<Datepicker
					v-if="isChangeMode"
					data-test="data-asset-date"
					v-model="model.dataAsset.date"
					:clearable="true"
					:teleport="true"
					:enable-time-picker="false"
					:format="getDateFormat()"
					:format-locale="formatLocale()"
					:select-text="$t('misc.select')"
					:cancel-text="$t('misc.cancel')"
					text-input
					class="input-box mb-1"
					:dark="theme === 'dark'"
				/>
				<span v-else>{{ model.dataAsset.date || '-' }}</span>
			</div>
		</div>

		<div v-if="canEditInstance" class="row">
			<label class="col-4 col-form-label">{{ t('institution') }}</label>
			<div class="col-8 d-flex flex-column">
				<InstanceSelect
					v-if="isChangeMode"
					data-test="data-asset-instance"
					v-model="model.dataAsset.instance"
					:class="{ 'is-invalid': !isValidInstance }"
					class="mb-1 w-100"
				/>
				<div v-if="isChangeMode" class="invalid-feedback">{{ $t('misc.validInstance') }}</div>
				<div v-else class="d-flex align-items-center justify-content-end flex-grow-1">
					<span class="text-end">{{ model.dataAsset.instance?.name || '-' }}</span>
				</div>
			</div>
		</div>

		<div class="row">
			<label class="col-4 col-form-label">{{ t('location') }}</label>
			<div class="col-8 d-flex align-items-center justify-content-end">
				<input v-if="isChangeMode" data-test="data-asset-location" class="form-control mb-2" v-model="model.dataAsset.location" />
				<span v-else>{{ model.dataAsset.location || '-' }}</span>
			</div>
		</div>

		<template v-if="!isChangeMode">
			<template v-for="field in custom" :key="field.id">
				<div class="row mt-2 text-wrap">
					<div class="col">{{ field.label }}</div>
					<div class="col d-flex align-items-center justify-content-end text-break">
						<CustomValueDisplay :field="field" :value="customValues?.[field.id]?.[model.dataAsset.id] ?? ''" />
					</div>
				</div>
			</template>
		</template>
		<template v-else>
			<template v-for="field in custom" :key="field.id">
				<div class="row mt-2 text-wrap">
					<div class="col">{{ field.label }}</div>
					<div class="col d-flex align-items-center justify-content-end text-break">
						<CustomValueEdit
							:field="field"
							:modelValue="customValues?.[field.id]?.[model.dataAsset.id] ?? ''"
							@update:modelValue="(val) => updateCustomValue(field.id, val ?? '')"
						/>
					</div>
				</div>
			</template>
		</template>

		<div class="row">
			<label class="col-4 col-form-label">{{ t('hasPersonalData') }}</label>
			<div class="col-8 d-flex align-items-center justify-content-end">
				<div v-if="isChangeMode" class="form-check form-switch">
					<input
						type="checkbox"
						data-test="data-asset-has-personal-data"
						class="form-check-input"
						v-model="model.dataAsset.hasPersonalData"
					/>
				</div>
				<div v-else>
					<i v-if="model.dataAsset.hasPersonalData" class="bi bi-circle-fill status-normal-background"></i>
					<i v-else class="bi bi-circle-fill status-verybig-background"></i>
				</div>
			</div>
		</div>
	</div>
	<div>
		<div class="col">
			<h6>{{ $t('reference.label', 2) }}</h6>
		</div>
	</div>
	<hr class="mt-2 mb-2 border-1" />
	<div class="row">
		<div class="col-5">
			<URLReference
				v-model="model.dataAsset.urlReferences"
				:owner-type="DataAssetReference"
				:owner-id="model.dataAsset.id"
				:can-edit="isEdit || isAdd"
			/>
		</div>

		<div class="col-auto d-flex">
			<div class="vr mx-3"></div>
		</div>

		<div class="col-5">
			<DocumentReference
				v-model="model.dataAsset.documentReferences"
				:owner-type="DataAssetReference"
				:owner-id="model.dataAsset.id"
				:can-edit="isEdit || isAdd"
			/>
		</div>
	</div>
	<hr class="mt-2 mb-2 border-1" />
	<div class="row">
		<div class="col-5">
			<h6>{{ t('asset.risks.default') }}</h6>

			<ul class="list-unstyled">
				<li v-for="risk in defaultConnectionRisks" :key="risk.metaId" class="d-flex align-items-center">
					<i class="bi bi-circle-fill me-2" :style="{ color: riskColor(risk) }"></i>
					<i v-if="risk.treatment === RT_Exclude" class="bi bi-x position-absolute text-white"></i>
					<span
						class="text-truncate text-nowrap overflow-hidden d-inline-block risks-cell"
						v-one-tooltip="risk.name"
						@click="goToRisks([risk.name])"
					>
						{{ risk.name }}
					</span>
				</li>
			</ul>
		</div>

		<div class="col-auto d-flex">
			<div class="vr mx-3"></div>
		</div>

		<div class="col-5">
			<h6>{{ t('asset.risks.connection') }}</h6>
			<ul class="list-unstyled">
				<li v-for="risk in assetConnectionRisks" :key="risk.metaId" class="d-flex align-items-center">
					<i class="bi bi-circle-fill me-2" :style="{ color: riskColor(risk) }"></i>
					<i v-if="risk.treatment === RT_Exclude" class="bi bi-x position-absolute text-white"></i>
					<span
						class="text-truncate text-nowrap overflow-hidden d-inline-block risks-cell"
						v-one-tooltip="risk.name"
						@click="goToRisks([risk.name])"
					>
						{{ risk.name }}
					</span>
				</li>
			</ul>
		</div>
	</div>

	<DataAssetConnections
		:can-edit="canEdit"
		:is-change-mode="isChangeMode"
		v-model="model"
		@cancel-edit="slider.setFooter(viewBtns)"
	></DataAssetConnections>
</template>
